import { useAuthStore } from "~/store/auth/auth.store";
import { isAuthLoggingOut } from "~/utils/helpers/auth/lifecycle";
import { forbiddenError } from "~/utils/route-access";

/**
 * Centralized handling for a role-downgrade HTTP 409.
 *
 * A 409 (Conflict) means the user's JWT roles no longer match the backend — a
 * server-side role downgrade. We NEVER log the user out: we re-sync the session
 * in place (re-mint the token + refetch the profile, which re-reads the current
 * DB roles) and show the "Vos droits ont été modifiés" error page. `roleConflictPending`
 * keeps that page up (auth.global re-asserts it on every navigation) until the
 * user acknowledges it via error.vue, so a competing navigation can't flicker it
 * away.
 */

// De-duplicate the burst of concurrent 409s a single downgrade produces.
let isHandling = false;

export const useErrorHandler = () => {
  /** Whether the error is an HTTP 409, across the shapes we receive (fetch / SDK / raw). */
  const isError409 = (error: any): boolean =>
    error?.status === 409 || error?.response?.status === 409 || error?.cause?.status === 409;

  /** Re-sync the session in place on a role-downgrade 409, then show a sticky error page. */
  const handleRoleConflict = async (): Promise<void> => {
    if (isHandling || isAuthLoggingOut()) return;
    isHandling = true;

    // Resolve context-dependent composables before the first await.
    const authStore = useAuthStore();
    const nuxtApp = useNuxtApp();

    try {
      // Re-mint the token + refetch the profile (current DB roles). Never logs out.
      const refreshed = await authStore.refreshAfterRoleConflict();
      if (isAuthLoggingOut()) return;

      // `false` means the session itself is gone (expired refresh token), not just the
      // roles. The sticky page would trap the user there — its only exit logs them out
      // anyway, via auth.global — so do it here, plainly.
      if (!refreshed) {
        await nuxtApp.runWithContext(() => authStore.handleSessionLoss());
        return;
      }

      authStore.roleConflictPending = true;
      nuxtApp.runWithContext(() => showError(forbiddenError()));
    } catch (error) {
      if (isAuthLoggingOut()) return;
      // The re-sync itself failed (network, 5xx). The roles we hold are the ones the
      // backend just rejected, so the user must not stay on this page: show the same
      // error page. The next navigation re-runs the check with a working connection.
      console.error("handleRoleConflict: role re-sync failed", error);
      authStore.roleConflictPending = true;
      nuxtApp.runWithContext(() => showError(forbiddenError()));
    } finally {
      isHandling = false;
    }
  };

  /** Route a 409 to the handler (reported as handled); leave other errors untouched. */
  const handleHttpError = (error: any): boolean => {
    if (!isError409(error)) return false;
    // Deliberately not awaited — the callers are synchronous error hooks. `handleRoleConflict`
    // handles its own failures; this is the backstop so nothing can escape as an unhandled
    // rejection into the `unhandledrejection` listener.
    handleRoleConflict().catch(() => {});
    return true;
  };

  return { handleHttpError, isError409, handleRoleConflict };
};
